import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import track from 'react-tracking';
import { FormattedMessage } from 'gatsby-plugin-react-intl';

import EmailOptIn from '@prospectus/common/ui/EmailOptIn';
import { formatProgramType } from '@utils/edxProductDisplay';
import { getLmsLink } from '@utils/edxLinks';
import { submitEvent, addTrackingToLink } from '@utils/analytics';
import { withLocation } from '@utils/navigation';
import withUser from '@utils/withUser';
import { translateVariant } from '@utils/cohesion/helpers/dataTranslationMatrices';
import tagularEvent from '@utils/cohesion/helpers/TagularEvent';
import { getCorrelationID, hyphenateForTagular } from '@utils/cohesion/helpers';

function ProgramPursueButton({
  applicableSeatTypes,
  authoringOrganizations,
  enrollBaseUrl,
  skus,
  courses,
  title,
  type,
  userEnrollments,
  userEntitlements,
  userLoggedIn,
  tracking,
  id,
  displayTrackModal,
  trackModalClickHandler,
  passedEmailOptIn,
  hideEmailOptIn,
  screenReaderText,
  uuid,
  search,
  webview,
  primarySubject,
}) {
  const [emailOptIn, setEmailOptIn] = useState(passedEmailOptIn || false);
  const [hasEnrollment, setHasEnrollment] = useState(false);
  const [hasSkus, setHasSkus] = useState(false);
  const [outstandingCourses, setOutstandingCourses] = useState([]);

  const linkRef = useRef(null);

  const isEnrolledInCourseWithCertificate = (courseRunKeys = []) => userEnrollments.some((enrollment) => {
    // is enrolled in the course
    const isEnrolled = courseRunKeys.some(
      (key) => enrollment.courseId === key,
    );
      /**
       * Check if any applicableSeatTypes are a substring of an entitlement mode
       * necessary for the no-id-professional case, which should count as an applicableSeatType
       */
    const isApplicableSeatType = applicableSeatTypes.some((seatType) => enrollment.mode.includes(seatType));

    return isEnrolled && isApplicableSeatType;
  });

  const isEntitledToCourse = (courseUuid) => userEntitlements.some(
    (entitlement) => entitlement.courseUuid === courseUuid
        && entitlement.expirationDate === null,
  );

  const userIsInCourseWithCertificate = (course) => {
    const { courseRunKeys, uuid: courseUuid } = course;

    return (
      isEnrolledInCourseWithCertificate(courseRunKeys)
      || isEntitledToCourse(courseUuid)
    );
  };

  /**
   * Retrieve an entitlement with applicable type from the course entitlements.
   */
  const getApplicableEntitlement = (entitlements = []) => entitlements.find(
    (entitlement) => (entitlement.expires == null || entitlement.expires > Date.now())
        && applicableSeatTypes.includes(entitlement.mode),
  );

  /**
   * Retrieve a seat with applicable seat type from the course run seats.
   */
  const getApplicableSeat = (seats = []) => seats.find((seat) => applicableSeatTypes.includes(seat.type));

  const getProductForCourse = (course) => {
    const entitlement = getApplicableEntitlement(course.entitlements);
    const { activeCourseRun } = course;
    const seat = activeCourseRun
      ? getApplicableSeat(activeCourseRun.seats)
      : null;

    return entitlement || seat;
  };

  /**
   * Get the courses in a program that the user has not enrolled in with a
   * certificate and does not have an entitlement for.
   * @returns {*}
   */
  const getOutstandingCourses = () => {
    let foundSkus = false;
    let foundEnrollment = false;

    const filteredCourses = courses
      //  reject courses user is enrolled in with a certificate
      .filter((courseNode) => {
        const userHasEnrollment = userIsInCourseWithCertificate(courseNode);

        if (userHasEnrollment) {
          foundEnrollment = true;
        }

        return !userHasEnrollment;
      })
      //  reject courses user has entitlements for
      .filter((courseNode) => {
        const sku = getProductForCourse(courseNode);
        const skuIsUndefined = sku === undefined;

        if (!skuIsUndefined) {
          foundSkus = true;
        }

        return !skuIsUndefined;
      });

    if (
      (foundSkus && hasSkus !== foundSkus)
      || (foundEnrollment && hasEnrollment !== foundEnrollment)
    ) {
      setHasSkus(foundSkus);
      setHasEnrollment(foundEnrollment);
    }

    return filteredCourses;
  };

  const updateOutstandingCourses = useCallback(() => {
    // Is user has no enrollment or entitlements return program course count
    const latestOutstandingCourses = userEnrollments.length + userEntitlements.length === 0
      ? courses
      : getOutstandingCourses();

    if (outstandingCourses !== latestOutstandingCourses) {
      setOutstandingCourses(latestOutstandingCourses);
    }
  }, [userEnrollments, userEntitlements, courses]);

  const getLinkProps = () => {
    const { queryID, index, position } = search;
    return {
      category: 'navigation',
      link: linkRef.current.getAttribute('href'),
      index,
      queryID,
      position,
      objectID: `program-${uuid}`,
      // Algolia requires the objectID to be in a 'products' property as well as top-level
      products: [{ objectID: `program-${uuid}` }],
    };
  };

  const toggleEmailOptIn = () => {
    setEmailOptIn(!emailOptIn);
  };

  useEffect(() => {
    // Outstanding courses are the courses in a program that the user has not enrolled in with a
    // certificate and does not have an entitlement for.
    updateOutstandingCourses();

    if (!displayTrackModal) {
      addTrackingToLink(
        tracking,
        'edx.bi.link.pursue-program.clicked',
        linkRef.current,
        getLinkProps,
      );

      if (hideEmailOptIn && passedEmailOptIn !== emailOptIn) {
        toggleEmailOptIn();
      }
    }
  }, []);

  const handlePursueProgramClick = (event) => {
    if (displayTrackModal) {
      trackModalClickHandler(emailOptIn);
      event.preventDefault();
      return false;
    }

    const outstandingEnrollmentCount = outstandingCourses.length;
    // no current enrollments in course programs
    if (!userLoggedIn || outstandingEnrollmentCount === courses.length) {
      let productBrand = 'unknown';

      if (authoringOrganizations.length >= 1) {
        productBrand = hyphenateForTagular(authoringOrganizations[0].name);
      }

      /** @type import("src/utils/cohesion/types/Tagular.ts").ConversionTracked */
      const conversionEvent = {
        correlation: { id: getCorrelationID(true) },
        metadata: {
          action: 'Started',
          category: 'Enrollment',
          timestamp: new Date().toISOString(),
          productList: [{
            brand: productBrand,
            category: hyphenateForTagular(primarySubject.slug),
            variant: translateVariant(type),
            name: title,
          }],
        },
      };
      tagularEvent('ConversionTracked', conversionEvent);
    }

    if (webview) {
      submitEvent(
        tracking,
        getLinkProps(),
        'edx.bi.link.pursue-program.clicked',
      );
    }

    return true;
  };

  const getSkuQueryStringForPartialBundle = () => outstandingCourses.reduce((queryString, courseNode) => {
    const entitlement = getProductForCourse(courseNode);

    if (entitlement !== undefined) {
      return `${queryString}&sku=${encodeURIComponent(entitlement.sku)}`;
    }

    return queryString;
  }, '');

  const getSkuQueryStringForFullBundle = () => skus.reduce((queryString, sku) => `${queryString}&sku=${sku}`, '');

  const getEnrollmentUrl = () => {
    if (displayTrackModal) {
      return '';
    }

    const skuQueryString = hasEnrollment || hasSkus
      ? getSkuQueryStringForPartialBundle()
      : getSkuQueryStringForFullBundle();

    return `${enrollBaseUrl}${skuQueryString}&email_opt_in=${emailOptIn}`;
  };

  const getEnrollmentButtonText = () => {
    const outstandingEnrollmentCount = outstandingCourses.length;

    // no current enrollments in course programs
    if (!userLoggedIn || outstandingEnrollmentCount === courses.length) {
      return (
        <FormattedMessage
          id="prospectus.program_details.pursue_program.button_text"
          description="Text for the button to pursue (enroll in) the program"
          defaultMessage="Pursue the Program"
        />
      );
    }
    // part of program already purchased
    if (outstandingEnrollmentCount > 0) {
      return (
        <FormattedMessage
          id="prospectus.program_details.continue_program.button_text"
          description="Text for the button to buy the remaining courses in the program"
          defaultMessage={`Buy Remaining {outstandingEnrollmentCount, plural,
                      one {Certificate}
                      other {Certificates}
                    }`}
          values={{ outstandingEnrollmentCount }}
        />
      );
    }
    // already fully enrolled
    return (
      <FormattedMessage
        id="prospectus.program_details.start_program.button_text"
        description="Text for the button to start (enroll in) the program"
        defaultMessage="Start the {programType}"
        values={{ programType: formatProgramType(type) }}
      />
    );
  };

  const outstandingEnrollmentCount = outstandingCourses.length;
  const userFullyEnrolled = userLoggedIn && outstandingEnrollmentCount === 0;

  const linkUrl = userFullyEnrolled
    ? getLmsLink('/dashboard/programs/')
    : getEnrollmentUrl();
  const label = getEnrollmentButtonText();
  const hasId = !!id;

  return (
    <div className="program-pursue-btn">
      <a
        href={linkUrl}
        className="enroll-btn btn btn-brand w-100"
        {...(hasId && { id })}
        {...(displayTrackModal && { role: 'button' })}
        ref={linkRef}
        onClick={handlePursueProgramClick}
      >
        {screenReaderText && (
          <span className="sr-only">{screenReaderText}</span>
        )}
        {label}
      </a>
      {!userFullyEnrolled && !hideEmailOptIn && (
        <EmailOptIn
          optIn={emailOptIn}
          partners={authoringOrganizations}
          title={title}
          toggleEmailOptIn={toggleEmailOptIn}
          {...(hasId && { id })}
        />
      )}
    </div>
  );
}

ProgramPursueButton.propTypes = {
  applicableSeatTypes: PropTypes.arrayOf(PropTypes.string),
  authoringOrganizations: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string,
      key: PropTypes.string,
      logoImageUrl: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  enrollBaseUrl: PropTypes.string.isRequired,
  skus: PropTypes.arrayOf(PropTypes.string).isRequired,
  courses: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string,
      courseRunKeys: PropTypes.arrayOf(PropTypes.string),
      topics: PropTypes.arrayOf(
        PropTypes.shape({
          topic: PropTypes.string.isRequired,
        }),
      ),
    }),
  ),
  title: PropTypes.string.isRequired,
  type: PropTypes.string,
  userEnrollments: PropTypes.arrayOf(
    PropTypes.shape({
      courseId: PropTypes.string.isRequired,
      mode: PropTypes.string.isRequired,
    }),
  ),
  userEntitlements: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      expirationDate: PropTypes.string,
      courseUuid: PropTypes.string.isRequired,
    }),
  ),
  userLoggedIn: PropTypes.bool,
  tracking: PropTypes.shape(),
  id: PropTypes.string,
  displayTrackModal: PropTypes.bool,
  trackModalClickHandler: PropTypes.func,
  passedEmailOptIn: PropTypes.bool,
  hideEmailOptIn: PropTypes.bool,
  screenReaderText: PropTypes.string,
  uuid: PropTypes.string.isRequired,
  search: PropTypes.shape({
    position: PropTypes.number,
    queryID: PropTypes.string,
    index: PropTypes.string,
  }),
  webview: PropTypes.bool,
  primarySubject: PropTypes.shape({
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
  }).isRequired,
};

ProgramPursueButton.defaultProps = {
  applicableSeatTypes: [],
  courses: [],
  type: undefined,
  userEnrollments: [],
  userEntitlements: [],
  userLoggedIn: false,
  tracking: {},
  id: undefined,
  displayTrackModal: false,
  trackModalClickHandler: () => {},
  passedEmailOptIn: false,
  hideEmailOptIn: false,
  screenReaderText: undefined,
  search: {},
  webview: false,
};

export { ProgramPursueButton as BaseProgramPursueButton };

export default track({
  component: 'program-pursue-button',
})(withUser(withLocation(ProgramPursueButton)));
